import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Chart as ChartJS, Filler, LinearScale, LineController, LineElement, PointElement, Tooltip, type ChartOptions, type TooltipModel } from "chart.js";
import { useEffect, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { VideoInsightScene, VideoInsightSceneScores } from "@/api/studio/insights/types/attention.type";
import { getTimeTicks } from "@/features/reports/utils/get-time-ticks";
import { asColor, asPlaybackTime } from "@/utils/format";
import { cn } from "@/utils/classes";

ChartJS.register(LinearScale, LineController, PointElement, LineElement, Filler, Tooltip);

type MetricKey = keyof VideoInsightSceneScores;

const METRICS: { key: MetricKey; label: string; color: string }[] = [
  { key: "attention", label: "Attention", color: "#19D3C8" },
  { key: "hook", label: "Hook", color: "#F2318A" },
  { key: "visualEase", label: "Visual ease", color: "#9CA3AF" },
  { key: "hold", label: "Hold", color: "#9747FF" },
];

// Locked to the same width as the chart's y-axis label gutter (forced via the y scale's
// afterFit below), so the scene marker row and time ruler line up with the plotted points.
const Y_AXIS_WIDTH = 30;

// How long a newly toggled-on metric's colour fades in for.
const FADE_IN_DURATION = 400;

interface AnimationDatasetContext {
  datasetIndex: number;
}

const TOOLTIP_ID = "video-attention-graph-tooltip";

/*
 * getOrCreateTooltipEl function
 * Returns the floating HTML tooltip element for a chart, creating and appending it to the
 * canvas's parent the first time it's needed. Chart.js's own tooltip only draws to canvas, so a
 * custom "external" tooltip needs its own DOM element to render richer HTML content into.
 */
const getOrCreateTooltipEl = (chart: ChartJS): HTMLDivElement => {
  const parent = chart.canvas.parentNode as HTMLElement;
  let tooltipEl = parent.querySelector<HTMLDivElement>(`#${TOOLTIP_ID}`);

  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = TOOLTIP_ID;
    tooltipEl.style.position = "absolute";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.transition = "opacity 0.1s ease";
    tooltipEl.style.transform = "translate(-50%, -100%)";
    tooltipEl.style.paddingBottom = "12px";
    tooltipEl.style.zIndex = "10";
    parent.appendChild(tooltipEl);
  }

  return tooltipEl;
};

// Muted grey (with a bit of opacity) for a metric's dot/value when its score isn't available for
// this scene yet — matches the side panel's own "Later" treatment.
const MUTED_DOT_COLOR = "#9ca3af";

/*
 * renderTooltipRow function
 * Renders one metric row of the hover tooltip: a label on the left, and either a coloured dot +
 * percentage (using the same asColor score-to-colour mapping as the side panel), or a muted dot +
 * fallback label (when the scene's score for that metric isn't computed yet) on the right.
 */
const renderTooltipRow = (label: string, score: number | null | undefined, nullLabel: string): string => {
  const hasScore = typeof score === "number";
  const dotHtml = hasScore
    ? `<span style="width:8px;height:8px;border-radius:9999px;background:${asColor(score)};display:inline-block"></span>`
    : `<span style="width:8px;height:8px;border-radius:9999px;background:${MUTED_DOT_COLOR};opacity:0.5;display:inline-block"></span>`;
  const valueHtml = hasScore ? `<span style="color:#e9e9ec">${Math.round(score)}%</span>` : `<span style="color:rgba(204,204,204,0.5)">${nullLabel}</span>`;

  return `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;padding:6px 0;">
      <span style="font-size:12px;font-weight:600;color:#e9e9ec;">${label}</span>
      <div style="display:flex;align-items:center;gap:8px;">
        ${dotHtml}
        <span style="font-size:12px;font-weight:600;">${valueHtml}</span>
      </div>
    </div>
  `;
};

/*
 * renderTooltipHtml function
 * Renders the full hover tooltip for one scene: its label, the Attention score as a mini progress
 * bar, and Hook/Visual ease/Hold as coloured-dot rows — always all four, regardless of which
 * metrics are currently toggled on in the graph. A null Hook score reads "Earlier" (the hook
 * window has already passed by this scene); a null Hold score reads "Later" (hold hasn't started).
 */
const renderTooltipHtml = (sceneLabel: string, scores: VideoInsightSceneScores): string => {
  const attention = scores.attention;
  const hasAttention = typeof attention === "number";
  const attentionColor = hasAttention ? asColor(attention) : MUTED_DOT_COLOR;
  const attentionWidth = hasAttention ? Math.round(attention) : 0;
  const attentionValue = hasAttention ? `${Math.round(attention)}%` : "--";

  return `
    <div style="background:#1d232a;border:1px solid #323c49;border-radius:8px;padding:12px;width:220px;box-shadow:0 10px 15px rgba(0,0,0,0.2);">
      <p style="font-size:12px;font-weight:600;color:#e9e9ec;opacity:0.7;margin:0 0 8px;">${sceneLabel}</p>
      <div style="background:#242c35;border-radius:8px;padding:6px 8px;display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:4px;">
        <span style="font-size:14px;font-weight:600;color:#e9e9ec;">Attention</span>
        <div style="display:flex;align-items:center;gap:12px;">
          <div style="width:64px;height:6px;border-radius:8px;background:#323c49;overflow:hidden;">
            <div style="width:${attentionWidth}%;height:100%;background:${attentionColor};"></div>
          </div>
          <span style="font-size:14px;font-weight:600;color:#e9e9ec;">${attentionValue}</span>
        </div>
      </div>
      ${renderTooltipRow("Hook", scores.hook, "Earlier")}
      ${renderTooltipRow("Visual ease", scores.visualEase, "Later")}
      ${renderTooltipRow("Hold", scores.hold, "Later")}
    </div>
  `;
};

/* Prop Types */
interface VideoAttentionGraphProps {
  scenes: VideoInsightScene[];
}

/**
 * VideoAttentionGraph component
 *
 * Plots the Attention/Hook/Visual ease/Hold scores over the video's duration as a toggleable area
 * chart, one point per scene. Attention is active by default; Hook, Visual ease, and Hold can each
 * be switched on or off independently. Below the chart, a row of numbered scene markers and a time
 * ruler give the x-axis context.
 */
export const VideoAttentionGraph = ({ scenes }: VideoAttentionGraphProps) => {
  /* State */
  const [activeMetrics, setActiveMetrics] = useState<Set<MetricKey>>(new Set(["attention"]));
  // Tracks which metrics were already active as of the last render, so only a metric that's
  // newly switched on this render fades in — otherwise every already-visible line replays the
  // fade whenever any other metric is toggled, since react-chartjs-2 passes a new datasets array.
  const previouslyActiveMetrics = useRef<Set<MetricKey>>(new Set());

  /* Handlers */
  const toggleMetric = (key: MetricKey) => {
    setActiveMetrics((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    previouslyActiveMetrics.current = activeMetrics;
  }, [activeMetrics]);

  if (scenes.length === 0) return null;

  /* Consts */
  const totalDuration = scenes.reduce((sum, scene) => sum + scene.duration, 0);
  const timeTicks = getTimeTicks(totalDuration);
  const activeMetricConfigs = METRICS.filter((metric) => activeMetrics.has(metric.key));

  // A dataset only plays its draw-in animation the render it's newly switched on — otherwise every
  // already-visible line would redraw itself whenever a sibling metric is toggled, since
  // react-chartjs-2 passes a brand new datasets array on every render.
  const isNewlyActiveDataset = (ctx: AnimationDatasetContext) => {
    const metric = activeMetricConfigs[ctx.datasetIndex];
    return !!metric && !previouslyActiveMetrics.current.has(metric.key);
  };

  // Renders a custom HTML tooltip (all four metrics, regardless of which are toggled on)
  // positioned over the hovered point, since Chart.js's built-in tooltip only draws to canvas.
  const handleTooltip = (context: { chart: ChartJS; tooltip: TooltipModel<"line"> }) => {
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltipEl(chart);

    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = "0";
      return;
    }

    // Every dataset's data array is [synthetic leading point, ...one per scene, synthetic
    // trailing point], so real scenes sit at indices 1..scenes.length inclusive.
    const dataIndex = tooltip.dataPoints?.[0]?.dataIndex ?? 0;
    const sceneIndex = dataIndex - 1;
    const scene = scenes[sceneIndex];
    if (!scene) {
      tooltipEl.style.opacity = "0";
      return;
    }

    tooltipEl.innerHTML = renderTooltipHtml(`Scene ${sceneIndex + 1}`, scene.scores);

    // caretX/caretY are already canvas-local pixel coordinates, and the tooltip is appended into
    // the canvas's own (position: relative) parent, so no viewport/scroll offset math is needed.
    tooltipEl.style.opacity = "1";
    tooltipEl.style.left = `${tooltip.caretX}px`;
    tooltipEl.style.top = `${tooltip.caretY}px`;
  };

  const chartData = {
    datasets: activeMetricConfigs.map((metric) => {
      const scenePoints = scenes.map((scene) => ({ x: scene.startTime + scene.duration / 2, y: scene.scores[metric.key] }));
      // Extend a flat line from the first/last scene's score out to the video's start/end, so the
      // plot doesn't visually stop short in the middle of the first and last scenes.
      const data = [{ x: 0, y: scenePoints[0].y }, ...scenePoints, { x: totalDuration, y: scenePoints[scenePoints.length - 1].y }];

      return {
        label: metric.label,
        data,
        borderColor: metric.color,
        backgroundColor: `${metric.color}26`,
        fill: true,
        tension: 0.4,
        spanGaps: true,
        // No point marker on the synthetic leading/trailing points, only on real scene points.
        pointRadius: data.map((_, index) => (index === 0 || index === data.length - 1 ? 0 : 4)),
        pointBackgroundColor: metric.color,
        pointBorderColor: "#242c35",
        pointBorderWidth: 2,
      };
    }),
  };

  const chartOptions: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    animations: {
      // Points/lines appear at their real position immediately; only their colour fades in, so
      // toggling a metric on doesn't grow the line up from the x-axis or draw itself in.
      x: { duration: 0 },
      y: { duration: 0 },
      colors: {
        type: "color",
        properties: ["borderColor", "backgroundColor", "pointBackgroundColor", "pointBorderColor"],
        from: "transparent",
        easing: "easeOutQuad",
        duration: (ctx) => (isNewlyActiveDataset(ctx as unknown as AnimationDatasetContext) ? FADE_IN_DURATION : 0),
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: totalDuration,
        display: false,
      },
      y: {
        min: 0,
        max: 100,
        afterFit: (scale) => {
          scale.width = Y_AXIS_WIDTH;
        },
        grid: { color: "rgba(255,255,255,0.08)" },
        ticks: {
          stepSize: 20,
          color: "rgba(233,233,236,0.5)",
          font: { size: 8 },
          callback: (value) => `${value}%`,
        },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: false,
        external: handleTooltip,
      },
    },
  };

  /* Render */
  return (
    <div className="flex flex-col gap-4 bg-[#242c35] rounded-lg p-4 min-w-0">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-semibold">Attention</h2>
        <div className="flex items-center gap-1.5 opacity-70">
          <span className="text-white text-xs font-semibold">About this graph</span>
          <InformationCircleIcon className="size-3.5 text-white" />
        </div>
      </div>

      <div className="flex gap-2">
        {METRICS.map((metric) => {
          const isActive = activeMetrics.has(metric.key);
          return (
            <button
              key={metric.key}
              type="button"
              onClick={() => toggleMetric(metric.key)}
              aria-pressed={isActive}
              className={cn("flex items-center gap-1.5 h-[31px] px-3 rounded-lg text-xs font-semibold text-white cursor-pointer", !isActive && "bg-[#323c49]")}
              style={isActive ? { backgroundColor: `${metric.color}B3` } : undefined}
            >
              {metric.label}
              <InformationCircleIcon className="size-4" />
            </button>
          );
        })}
      </div>

      <div className="min-w-0" style={{ height: 149, position: "relative" }}>
        <Line data={chartData} options={chartOptions} />
      </div>

      {/* Scene markers */}
      <div className="flex gap-0.5 w-full" style={{ paddingLeft: Y_AXIS_WIDTH }}>
        {scenes.map((scene, index) => (
          <div
            key={scene.scene}
            className="bg-[#323c49] rounded text-white text-[10px] font-medium text-center py-1"
            style={{ width: `${(scene.duration / totalDuration) * 100}%` }}
          >
            {index + 1}
          </div>
        ))}
      </div>

      {/* Time ruler */}
      <div className="relative h-4 w-full" style={{ marginLeft: Y_AXIS_WIDTH, width: `calc(100% - ${Y_AXIS_WIDTH}px)` }}>
        {timeTicks.map((tick) => (
          <span key={tick} className="absolute -translate-x-1/2 text-[8px] text-white/50" style={{ left: `${(tick / totalDuration) * 100}%` }}>
            {asPlaybackTime(tick)}
          </span>
        ))}
      </div>
    </div>
  );
};
